import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Column } from 'components/display/Table/types';
import { NsKey } from 'enums/NsKey';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import GenericBadge from 'components/ui/Badge';
import { Button } from 'components/ui/Button';
import { EyeIcon } from 'components/icons/Icons';
import MoreBubble from 'components/ui/Bubble';
import {
  SimulationRow,
  SimulationIdentifier
} from 'features/simulation-view/types/interfaces';
import { SimulationTableRowKey } from 'features/simulation-view/types/enums';
import { Status } from 'types/optim/status';
import ServerTable from 'shared/contexts/table-context/components/ServerTable';
import { SimulationFetchParams } from 'slices/generic-table/hooks/useSimulationTable';
import { mapRiskSummariesToSimulations } from 'features/simulation-view/mappers/mapRiskSummaryToSimulation';
import {
  GetRiskListResponseDto,
  GetGenericListCommandDto,
  BranchCode,
  StepIdentifier,
  IdLabel
} from 'api/risk';
import { risksAPI } from 'services/swagger-api/riskApi';
import { TableResponse } from 'slices/generic-table/genericTableSlice';
import {
  buildRoute,
  getStepRouteByStepIdentifierString
} from 'features/subscription-process/utils/getRouteByStepIdentifier';
import { Calculator } from 'lucide-react';
import { DocumentScopeEnum } from 'api/actor';
import { useRoleCan } from 'core/hooks/useRoleCan';
import { Capability } from 'core/roles';

interface PersonSimulationsProps {
  personId: string;
}

export const PersonSimulations: React.FC<PersonSimulationsProps> = ({
  personId
}) => {
  const { t } = useDynamicTranslation([NsKey.Common, NsKey.Risk]);
  const navigate = useNavigate();
  const [isNavigating, setIsNavigating] = useState(false);
  // Mêmes droits que la liste simulations globale (matrice centrale) : Courtier only.
  const canOpenProcess = useRoleCan(Capability.SimulationOpen);
  const canRowActions = useRoleCan(Capability.SimulationActions);

  const goToBorrowerSimulation = useCallback(
    async (borrowerProjectId: string, borrowerId: string) => {
      if (!canOpenProcess) return;
      if (isNavigating) return;
      setIsNavigating(true);

      try {
        const response = await risksAPI.getSubscriptionProcessBorrower({
          projetEmpruntId: borrowerProjectId
        });

        const highestAccessibleStep =
          response?.subscriptionState?.highestAccessibleStep;
        if (!highestAccessibleStep) return;

        const routeConfig = getStepRouteByStepIdentifierString(
          BranchCode.Borrower,
          response.subscriptionState.highestAccessibleStep
        );
        if (!routeConfig) return;

        const isContractualization =
          highestAccessibleStep === StepIdentifier.Contractualization ||
          highestAccessibleStep === StepIdentifier.SignedContract;

        const quoteId = isContractualization
          ? response.subscriptionState.steps.find(
              (step) => step.identifier === StepIdentifier.Contractualization
            )?.subSteps[0].data?.quoteId || ''
          : undefined;

        const route = buildRoute(routeConfig.route, {
          borrowerProjectId,
          ...(quoteId && { quoteId }),
          ...(borrowerId && { riskId: borrowerId })
        });
        navigate(route);
      } catch (error) {
        console.error('Failed to fetch subscription process:', error);
      } finally {
        setIsNavigating(false);
      }
    },
    [navigate, isNavigating, canOpenProcess]
  );

  const goToSimulation = useCallback(
    async (
      branchCode: BranchCode,
      riskId: string,
      personIdParam?: string,
      borrowerProjectId?: string
    ) => {
      if (!canOpenProcess) return;
      if (branchCode === BranchCode.Borrower) {
        // Projet d'emprunt explicite (champ racine de la liste) ; repli legacy : l'id.
        return goToBorrowerSimulation(borrowerProjectId ?? riskId, personIdParam || '');
      }

      if (isNavigating) return;
      setIsNavigating(true);

      try {
        const response = await risksAPI.getSubscriptionProcess({ riskId });

        const highestAccessibleStep =
          response?.subscriptionState?.highestAccessibleStep;
        if (!highestAccessibleStep) return;

        const routeConfig = getStepRouteByStepIdentifierString(
          branchCode,
          response.subscriptionState.highestAccessibleStep
        );
        if (!routeConfig) return;

        const isContractualization =
          highestAccessibleStep === StepIdentifier.Contractualization ||
          highestAccessibleStep === StepIdentifier.SignedContract;

        const quoteId = isContractualization
          ? response.subscriptionState.steps.find(
              (step) => step.identifier === StepIdentifier.Contractualization
            )?.subSteps[0].data?.quoteId || ''
          : undefined;

        const route = buildRoute(routeConfig.route, {
          riskId,
          ...(quoteId && { quoteId })
        });
        navigate(route);
      } catch (error) {
        console.error('Failed to fetch subscription process:', error);
      } finally {
        setIsNavigating(false);
      }
    },
    [navigate, isNavigating, goToBorrowerSimulation]
  );

  const columns: Column<SimulationRow>[] = useMemo(
    () => [
      {
        key: SimulationTableRowKey.DATE,
        label: t('COMMON.DATE'),
        sortable: true,
        render: (value) => {
          const date = new Date(value as string);
          const day = date.getDate();
          const month = date.toLocaleDateString('fr-FR', { month: 'short' });
          const year = date.getFullYear();
          const time = date.toLocaleTimeString('fr-FR', {
            hour: '2-digit',
            minute: '2-digit'
          });

          return (
            <div className="text-left">
              <div className="text-sm font-medium">
                {day} {month} {year}
              </div>
              <div className="text-xs text-gray-500">{time}</div>
            </div>
          );
        }
      },
      {
        key: SimulationTableRowKey.FOLDER_IDENTIFIER,
        label: t('RISK.LABELS.SIMULATION_NUMBER'),
        sortable: true,
        render: (value, row) => {
          const simulationIdentifier = value as SimulationIdentifier;
          return (
            <div
              onClick={
                canOpenProcess
                  ? () =>
                      goToSimulation(
                        simulationIdentifier.branchCode,
                        simulationIdentifier.id,
                        row.riskId,
                        row.borrowerProjectId
                      )
                  : undefined
              }
              className={
                canOpenProcess
                  ? 'cursor-pointer font-bold text-primary hover:underline'
                  : 'font-bold text-gray-700'
              }
            >
              {simulationIdentifier && simulationIdentifier.label}
            </div>
          );
        }
      },
      {
        key: SimulationTableRowKey.PRODUCTS,
        label: t('COMMON.LABELS.PRODUCTS')
      },
      {
        key: SimulationTableRowKey.QUOTE_CONTRACT,
        label: t('RISK.LABELS.CONTRACT_QUOTE_NUMBER'),
        render: (value, row) => {
          const bubbleItems = (value as IdLabel[]).map((item) => ({
            label: item.label,
            action: canOpenProcess
              ? () => navigate(`/simulation/contractualisation/${row.riskId}/${item.id}`)
              : undefined
          }));
          return <MoreBubble items={bubbleItems} />;
        }
      },
      {
        key: SimulationTableRowKey.STATUS,
        label: t('COMMON.STATUS'),
        sortable: true,
        render: (value) => {
          const status = value as Status;
          return (
            <GenericBadge
              label={status.label}
              color={status.color}
              badgeType="sm-bold"
            />
          );
        }
      }
    ],
    [t, navigate, goToSimulation, canOpenProcess]
  );

  const actions = useMemo(
    () => ({
      row: [
        {
          label: 'seeMore',
          render: (row: SimulationRow, index: number) => {
            const simulationIdentifier = row.folderIdentifier;
            return (
              <Button
                key={index}
                color="slate"
                round
                onClick={() =>
                  goToSimulation(
                    simulationIdentifier.branchCode,
                    simulationIdentifier.id,
                    row.riskId,
                    row.borrowerProjectId
                  )
                }
              >
                <EyeIcon />
              </Button>
            );
          }
        }
      ]
    }),
    [goToSimulation]
  );

  const fetchSimulations = useCallback(
    async (
      params: SimulationFetchParams
    ): Promise<TableResponse<SimulationRow>> => {
      try {
        const command: GetGenericListCommandDto = {
          pageNumber: params.pageNumber,
          pageSize: params.pageSize,
          searchString: params.searchString || undefined,
          sorting: params.sorting as any,
          filters: {
            personIds: [personId]
          }
        };

        const result: GetRiskListResponseDto = await risksAPI.getSimulations({
          getGenericListCommandDto: command
        });

        const mappedSimulations = mapRiskSummariesToSimulations(
          result.items || []
        );

        return {
          items: mappedSimulations,
          totalItems: result.totalCount || mappedSimulations.length,
          totalPages: Math.ceil(
            (result.totalCount || mappedSimulations.length) / params.pageSize
          ),
          currentPage: params.pageNumber
        };
      } catch (error) {
        console.error('Erreur lors de la récupération des simulations:', error);
        throw error;
      }
    },
    [personId]
  );

  return (
    <div className="bg-white rounded-xl shadow-sm border">
      <div className="flex items-center gap-3 p-4 border-b">
        <Calculator className="w-5 h-5 text-primary" />
        <h3 className="text-lg font-semibold text-gray-900">
          {t('FEATURES.TITLE.MY_SIMULATIONS')}
        </h3>
      </div>
      <div className="p-4">
        <ServerTable<SimulationRow>
          apiConfig={{
            fetchData: fetchSimulations
          }}
          columns={columns}
          actions={canRowActions ? actions : undefined}
          onRowClick={
            canOpenProcess
              ? (row) =>
                  goToSimulation(
                    row.folderIdentifier.branchCode,
                    row.folderIdentifier.id,
                    row.riskId,
                    row.borrowerProjectId
                  )
              : undefined
          }
        />
      </div>
    </div>
  );
};
